import React, { useCallback, useEffect, useState } from 'react';
import ModalShell from '../../shared/components/modal/ModalShell';
import ModalFormFooter from '../../shared/components/modal/ModalFormFooter';
import FormField from '../../shared/components/modal/FormField';
import SearchableSelect from '../../shared/components/table/SearchableSelect';
import useFormState from '../../shared/hooks/useFormState';
import useUserStore from '../../store/user-store';
import useOrganisationStore from '../../store/organisations-store';
import { trimData } from '../../shared/lib/formatters';

const UserTemplateFormModal = ({
  isOpen,
  onClose,
  mode = 'create',
  title,
  initialValues,
  initialOwnerName = '',
  onSubmit,
  tools,
}) => {
  const searchUsers = useUserStore((s) => s.searchUsers);
  const searchOrganisations = useOrganisationStore((s) => s.searchOrganisations);

  const { formData, handleChange, setField, isLoading, setIsLoading, isCreateDisabled, isSubmitDisabled } = useFormState({
    initialValues,
    isOpen,
    mode,
  });

  const [ownerLabel, setOwnerLabel] = useState(initialOwnerName);

  useEffect(() => {
    if (isOpen) {
      setOwnerLabel(initialOwnerName || '');
    }
  }, [isOpen, initialOwnerName]);

  const loadUserOptions = useCallback(
    (search) => searchUsers({ search }),
    [searchUsers]
  );

  const loadOrgOptions = useCallback(
    (search) => searchOrganisations({ search }),
    [searchOrganisations]
  );

  const handleCancel = () => onClose();

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await onSubmit(trimData(formData));
    } finally {
      setIsLoading(false);
    }
  };

  const requiredFields = [
    { name: 'name', required: true },
    { name: 'value', required: true },
    { name: 'tool_id', required: true },
    { name: 'owner_id', required: true },
  ];

  const disabled = isLoading || (mode === 'create' ? isCreateDisabled(requiredFields) : isSubmitDisabled);

  const handleOwnerTypeChange = (e) => {
    setField('owner_type', e.target.value);
    setField('owner_id', '');
    setOwnerLabel('');
  };

  const footer = (
    <ModalFormFooter
      onCancel={handleCancel}
      onSubmit={handleSave}
      isLoading={isLoading}
      isDisabled={disabled}
      submitLabel={mode === 'edit' ? 'Save Changes' : 'Create'}
      loadingLabel={mode === 'edit' ? 'Saving...' : 'Creating...'}
    />
  );

  const toolOptions = (tools || []).map((tool) => ({ value: tool.id, label: tool.name }));
  const isUserOwner = formData.owner_type === 'user';

  return (
    <ModalShell isOpen={isOpen} onClose={handleCancel} title={title} footer={footer} maxWidthClass="max-w-xl">
      <form onSubmit={(e) => e.preventDefault()}>
        <div className="flex flex-col gap-2">
          <FormField
            field={{ name: 'name', label: 'Name', type: 'text', required: true }}
            value={formData.name}
            onChange={handleChange}
          />
          <FormField
            field={{ name: 'value', label: 'Value', type: 'textarea', required: true, rows: 8 }}
            value={formData.value}
            onChange={handleChange}
          />
          <div className="mb-2 flex flex-col">
            <label className="mb-2 block text-sm font-bold text-gray-700">Owner</label>
            <div className="mb-2 flex gap-4">
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="owner_type"
                  value="user"
                  checked={formData.owner_type === 'user'}
                  onChange={handleOwnerTypeChange}
                  className="h-4 w-4 accent-indigo-600"
                />
                User
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="owner_type"
                  value="org"
                  checked={formData.owner_type === 'org'}
                  onChange={handleOwnerTypeChange}
                  className="h-4 w-4 accent-indigo-600"
                />
                Organisation
              </label>
            </div>
            <SearchableSelect
              key={formData.owner_type}
              name="owner_id"
              value={formData.owner_id}
              selectedLabel={ownerLabel}
              onChange={(id, option) => {
                setField('owner_id', id || '');
                setOwnerLabel(option?.name || '');
              }}
              loadOptions={isUserOwner ? loadUserOptions : loadOrgOptions}
              placeholder={isUserOwner ? 'Select User' : 'Select Organisation'}
              className="w-full"
              required
            />
          </div>
          <FormField
            field={{
              name: 'tool_id',
              label: 'Tool',
              type: 'select',
              options: toolOptions,
              placeholder: 'Select Tool',
            }}
            value={formData.tool_id}
            onChange={handleChange}
          />
        </div>
      </form>
    </ModalShell>
  );
};

export default UserTemplateFormModal;
